import Cookies from 'js-cookie'
import createFuzzySearch from '@nozbe/microfuzz'
import * as utils from '@/components/text-fragments.js';
import { setWithExpiry, getWithExpiry } from '@/utilities/helpers';
import { ENDPOINTS } from '@/components/constants';
import Swal from 'sweetalert2/dist/sweetalert2.js'
import { API } from '@/utilities/api';
import { PinchZoom } from '@/components/pinchZoom';

const Toast = Swal.mixin({
    toast: true,
    position: 'bottom-right',
    iconColor: 'white',
    customClass: { popup: 'colored-toast' },
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
});

/**
 * Highlights the specified ranges in the document.
 *
 * @param {Range[]} ranges - An array of Range objects to be highlighted.
 */
function highlightRanges(ranges) {
    const selection = window.getSelection();
    selection.removeAllRanges();
    ranges.forEach(range => selection.addRange(range));
}


/**
 * Applies a fragment directive to the document.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Document} params.document - The document to apply the fragment directive to.
 * @param {Object} params.documentFragmentDirective - The fragment directive to apply.
 */
function applyFragmentDirective({ document, documentFragmentDirective }) {
    if (documentFragmentDirective !== null) {
        const { documentIndicatedPart, ranges } = utils.indicatedPartOfTheDocument_beginning({
            document,
            documentFragmentDirective,
            documentAllowTextFragmentDirective: true,
        }) || {};

        if (documentIndicatedPart !== undefined) {
            utils.scrollToTheFragment(documentIndicatedPart);
        }


        if (ranges !== undefined) {
            highlightRanges(ranges);
        }
    }
}


/**
 * Applies a fragment directive string to the document.
 *
 * @param {string} fragmentDirective - The fragment directive string to be applied, e.g. ":~:text=bla&text=blub".
 * @throws {TypeError} Throws an error if the fragment directive is not a string or does not contain ':~:'.
 */
export function applyFragDir(fragmentDirective) {
    if (typeof fragmentDirective !== 'string' || !fragmentDirective.includes(':~:'))
        throw new TypeError('Expected a fragment directive string, e.g. ":~:text=bla&text=blub"');
    fragmentDirective = fragmentDirective.substring(fragmentDirective.indexOf(':~:') + 3);
    applyFragmentDirective({ document, documentFragmentDirective: fragmentDirective });
}


/**
 * OmniSearch class provides functionality for an omnibox search interface.
 * It handles user input, search history, and interactions with various search endpoints.SEARCH_
 * 
 * @class
 * @param {HTMLElement} element - The root element containing the search input, button, and results container.
 */
export class OmniSearch {

    /**
     *
     * Constructor
     *
     * @param {element} input - The input element to get the user query from
     * @param {element} element - The suggestion box element
     */
    constructor(element) {

        this.element = element;
        this.input = this.element.querySelector('.js-omni-search-input');
        this.button = this.element.querySelector('.js-omni-search-button');
        this.results = this.element.querySelector('.js-omni-search-results');

        this.search_active = false;
        this.ai_search_active = false;
        this.loading_last_search = false;
        this.specific_search_active = false;

        this.typing_speed = 0;
        this.show_more_images = false;
        this.ai_search_type = 'medium';
        this.loaded_latest_search = false;

        this.randomise_images = window.randomise_images || false;
        this.egress_stop_words = window.egress_stop_words || [];
        this.ingress_stop_words = window.ingress_stop_words || [];

        this.abort_controller = undefined;
        this.search_start = new Date().getTime();

        this.login_attempts = 0;
        this.saved_search_review_id = null;

        this.contextual_search_available = true;
        this.has_fetched_contextual_data = false;

        this.initial_images_array = [3782, 14498, 14483, 1291, 1332, 3280, 2789, 3236, 3696, 3689, 2352, 2386, 1556, 1737, 1719, 1351, 1415, 1413, 1414, 2181, 1523, 2431, 19953, 3485, 1095, 1631, 3668, 2819, 2849, 2843, 2818, 15991, 11310, 5191, 5868, 11521, 12969, 15150, 10687, 9687, 21508, 15043, 11897, 11921, 8702, 12071, 6679, 15212, 4318, 14705, 11009, 11498, 13244, 14381, 2735, 3240, 3352, 3357, 4606, 6380];

        this.current_tab = 'all';

        this.init();

    }


    /**
     * Stores the latest search query and results in local storage.
     */
    storeLatestSearch() {
        localStorage.setItem('dermnet-omni-search-stored', JSON.stringify({
            query: this.input.value,
            ai_search_results: this.stored_ai_search_results,
            search_results: this.stored_search_results,
            specific_search_results: this.stored_specific_search_results,
            contextual_search_active: this.contextual_search_active,
            contextual_search_data: this.contextual_search_data,
            contextual_search_topic: this.contextual_search_topic,
            contextual_search_url: this.contextual_search_url
        }));
    }

    removeLatestSearch() {
        localStorage.removeItem('dermnet-omni-search-stored');
        localStorage.removeItem('omni-searchscroll-position');
        this.loaded_latest_search = false;
        this.stored_ai_search_results = null;
        this.stored_search_results = null;
        this.stored_specific_search_results = null;
        this.contextual_search_active = false;
        this.contextual_search_data = null;
        this.contextual_search_topic = null;
        this.contextual_search_url = null;
        this.input.value = '';
        this.results.querySelector('.frame__search__scroll').scrollTo(0, 0);
    }

    changeTabs(tab = 'all') {

        this.current_tab = tab;

        this.element.querySelectorAll('.js-search-tab').forEach(tab => {
            tab.classList.remove('active');
        });

        this.element.querySelector(`.js-search-tab[data-tab="${tab}"]`).classList.add('active');

        this.element.setAttribute('data-current-tab', tab);

        if (tab === 'images') {
            this.show_more_images = true;
        } else {
            this.show_more_images = false;
        }

        if (tab === 'diagnosis') {
            document.body.classList.add('omni-search-diagnosis');
        } else {
            document.body.classList.remove('omni-search-diagnosis');
        }

        this.handleImages();

    }


    /**
     * Retrieves the latest search query and results from local storage.
     *
     * @returns {Object|null} The stored search data, or null if no data is found.
     */
    retrieveLastSearch() {
        return JSON.parse(localStorage.getItem('dermnet-omni-search-stored'));
    }


    /**
     * Retrieves the search history from local storage.
     * 
     * @returns {Array} An array of search history items. If no search history is found, returns an empty array.
     */
    getSearchHistory() {
        if (!localStorage.getItem('dermnet-omni-search-history')) return [];
        return JSON.parse(localStorage.getItem('dermnet-omni-search-history'));
    }



    /**
     * Removes the search history from local storage.
     * This function deletes the item with the key 'dermnet-omni-search-history' from local storage.
     */
    removeSearchHistory() {
        localStorage.removeItem('dermnet-omni-search-history');
    }


    /**
     * Calculates the time elapsed since a given date and returns a human-readable string.
     *
     * @param {string|Date} date - The date to calculate the time elapsed from. Can be a date string or a Date object.
     * @returns {string} A string representing the time elapsed since the given date in a human-readable format.
     *                    Possible values include "years ago", "months ago", "days ago", "hours ago", "minutes ago", 
     *                    or "under a minute ago".
     */
    timeAgo(date) {

        const now = new Date();
        const seconds = Math.floor((now - new Date(date)) / 1000);

        const intervals = [
            { label: "year", seconds: 31536000 },
            { label: "month", seconds: 2592000 },
            { label: "day", seconds: 86400 },
            { label: "hour", seconds: 3600 },
            { label: "minute", seconds: 60 }
        ];

        for (const interval of intervals) {
            const count = Math.floor(seconds / interval.seconds);
            if (count > 1) {
                return `${count} ${interval.label}s ago`;
            } else if (count === 1) {
                return `1 ${interval.label} ago`;
            }
        }

        return "under a minute ago";
    }






    /**
    * Scrolls the document to the currently selected text fragment.
    */
    scrollToTextFragment() {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        const elementToScroll = range.startContainer.nodeType === Node.TEXT_NODE
            ? range.startContainer.parentElement
            : range.startContainer;

        setTimeout(() => {
            elementToScroll?.scrollIntoView({
                // behavior: 'smooth',
                behavior: 'auto',
                block: 'center',
                inline: 'nearest'
            });
        }, 50);

        setTimeout(() => {
            window.scrollBy(0, 1);
            window.scrollBy(0, -1);
        }, 1000);

        setTimeout(() => {
            window.scrollBy(0, 1);
            window.scrollBy(0, -1);
        }, 2500);

        setTimeout(() => {
            window.scrollBy(0, 1);
            window.scrollBy(0, -1);
        }, 5000);


    }


    /**
     * 
     * Go to a specific text fragment
     * 
     * @param {event} e 
     * @param {string} href 
     * @returns 
     */
    goToTextFragment(e, href) {

        // Hide the search box
        this.hide();

        const url = new URL(href);

        // Check if the href matches the current page (excluding "/topics")
        if (url.pathname === window.location.pathname && window.location.pathname !== '/topics') {

            e.preventDefault();

            // Extract the snippet from the URL
            const snippet = new URL(href, window.location.origin).searchParams.get('snippet');

            // If there is no snippet, return early
            if (!snippet) return;

            // Update the URL with the snippet parameter
            history.pushState({}, '', `${window.location.pathname}?snippet=${snippet}`);

            // Apply the fragment directive and scroll to the text fragment
            applyFragDir(`:~:text=${snippet}`);

            // Scroll to the text fragment
            this.scrollToTextFragment();
        }
    }


    /**
     *
     * Initialise the suggestion box interactions
     *
     */
    init() {

        // close modal on escape key press
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                document.body.classList.remove('gallery-modal-open');

                //Reset the zoom of the page
                window.resetZoom();

                document.body.classList.remove('copyright-visible');
            }
        });

        // Show the ai more section if it was previously shown
        // if (localStorage.getItem('ai-more-shown')) {
        this.element.classList.add('ai-more-shown');
        // }

        // Store the scroll position for users to return to
        this.results.querySelector('.frame__search__scroll').addEventListener('scroll', () => {
            // store the scroll position for later use
            localStorage.setItem('omni-searchscroll-position', this.results.querySelector('.frame__search__scroll').scrollTop);
        });

        // Load up the latest search string only 
        if (this.retrieveLastSearch()) {
            this.query = this.retrieveLastSearch().query;
            this.input.value = this.query;
        }

        // Fetch the list of topics on initial focus
        this.input.addEventListener('focus', () => {
            this.show();

            if (this.input.value.length > 0) {
                this.element.querySelector('.js-omni-search-clear').style.display = 'block';
            };
        });

        // On click of the search button
        this.button.addEventListener('click', (e) => {
            e.preventDefault();
            this.results.querySelector('.frame__search__scroll').scrollTo(0, 0);
            this.search();
        })

        // On click of the AI search type buttons
        this.element.querySelectorAll('.js-search-type').forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                this.element.querySelectorAll('.js-search-type').forEach(button => button.classList.remove('selected'));
                button.classList.add('selected');
                this.ai_search_type = button.dataset.searchType;
                this.searchAI();
            });
        });

        // On click of the gallery modal buttons
        document.body.addEventListener('click', (e) => {

            if (e.target.matches('.js-suggested-search')) {
                e.preventDefault();
                this.input.value = e.target.dataset.query;
                this.search();
            }

            if (e.target.matches('.js-omni-search-clear')) {
                e.preventDefault();
                this.input.value = '';
                this.input.focus();
                document.body.classList.remove('omni-snippets-present');

                // scroll to the top of the results
                this.results.querySelector('.frame__search__scroll').scrollTo(0, 0);
                setTimeout(() => this.element.querySelector('.js-omni-search-clear').style.display = 'none', 100);
                this.removeLatestSearch();
            }

            if (e.target.matches('.gallery-modal__wrap__previous-alt')) {
                e.preventDefault();
                this.previousImage();
            }

            if (e.target.matches('.js-view-all-images-tab')) {
                e.preventDefault();
                document.querySelector('.js-search-tab[data-tab="images"]').click();
            }

            if (e.target.matches('.gallery-modal__wrap__next-alt')) {
                e.preventDefault();
                this.nextImage();
            }

            if (e.target.matches('.js-search-tab')) {
                e.preventDefault();
                this.changeTabs(e.target.dataset.tab);
            }

            if (e.target.classList.contains('frame__search__snippet')) {
                this.goToTextFragment(e, e.target.href);
            }

            if (e.target.parentElement.classList.contains('frame__search__snippet')) {
                this.goToTextFragment(e, e.target.parentElement.href);
            }

            if (e.target.classList.contains('js-flag-search')) {
                this.flagAISearch(e);
            }

            if (e.target.classList.contains('js-contextual-search-back')) {
                this.exitContextualSearch();
            }

            if (e.target.classList.contains('js-search-feedback')) {

                // Hide the results
                this.hide();

                // Show the feedback modal
                Swal.fire({
                    title: 'Give feedback on this search result',
                    showCancelButton: true,
                    cancelButtonText: 'Cancel',
                    confirmButtonText: 'Submit feedback',
                    input: 'textarea',
                    customClass: { container: 'swal-wide' },
                    inputLabel: '',
                    inputPlaceholder: 'What could be improved with this search result?',
                }).then((result) => {
                    if (result.isConfirmed) {
                        result.value
                        this.AISearchFeedback(e, result.value);
                    }
                })
            }

            if (e.target.classList.contains('js-enter-contextual-search')) {
                e.preventDefault();
                this.enterContextualSearch();
            }

            if (e.target.classList.contains('js-toggle-image-favourite-alt')) {
                let entity_id = e.target.dataset.entityId;
                let entity_type = 'image';

                API.toggle_favourites({ entity_id, entity_type })
                    .then((response) => {

                        e.target.classList.toggle('hearted');

                        if (response.status !== 'ok' || response.message === 401) {
                            e.target.classList.remove('hearted');
                            Toast.fire({
                                icon: (response.status === 'ok') ? 'success' : 'error',
                                title: (response.message !== 401) ? response.message : 'You need to be logged in as a pro member to favourite images'
                            });
                        } else {
                            this.fetchFavouriteImages();

                        }


                    });


            }
        });

        setTimeout(() => {
            // Extract the snippet from the URL
            const snippet = new URL(window.location.href).searchParams.get('snippet');

            // if there is a snippet url param
            if (snippet) {
                // let snippet = window.location.search.split('snippet=')[1];
                applyFragDir(`:~:text=${snippet}`);
                this.scrollToTextFragment();
            }
        }, 1);

        this.element.addEventListener('click', (e) => {

            // If click on element or if its parent is the element
            if (e.target.classList.contains('js-omni-search-suggestion')) {
                e.preventDefault();
                this.input.value = e.target.dataset.query;
                this.search();
            }

            // If click on element or if its parent is the element
            if (e.target.parentElement.classList.contains('js-omni-search-suggestion')) {
                e.preventDefault();
                this.input.value = e.target.parentElement.dataset.query;
                this.search();
            }

            // If click on the show hide search words
            if (e.target.classList.contains('js-show-hide-search-words')) {
                e.target.classList.toggle('hidden');
                this.updateHighlightedSearchWords();
            }

            // If click on an image element
            if (e.target.classList.contains('js-omni-image')) {
                e.preventDefault();

                // Clean up any pre existing modals
                this.clean();

                // Get index of clicked item
                let grid_item = e.target.parentElement.parentElement;
                this.current_index = parseInt(grid_item.dataset.index);

                // Append the template to the end of the page
                document.body.insertAdjacentHTML('beforeend', this.templateModal(this.current_index));

                // Show the modal after a short delay
                setTimeout(() => this.showImagesModal(), 100);



            }

        });

        // On Enter keydown of the input
        this.input.addEventListener('keydown', (e) => {

            if (this.input.value.length > 0) {
                // replace any ’ with '
                this.input.value = this.input.value.replace(/’/g, "'");
            }


            if (this.input.value.length > 0) {
                this.element.querySelector('.js-omni-search-clear').style.display = 'block';
            } else {
                this.element.querySelector('.js-omni-search-clear').style.display = 'none';
            }

            if (e.key === 'Enter') {
                e.preventDefault();
                this.results.querySelector('.frame__search__scroll').scrollTo(0, 0);
                this.search();
            }

            if (e.key === 'Escape') {
                if (this.contextual_search_active) {
                    this.exitContextualSearch();
                } else {
                    this.hide();
                    this.input.blur();
                }
            }

        });



        // Keydown event listeners
        document.addEventListener('keydown', (e) => {

            // If the meta key and k are pressed
            if (e.key === 'k' && e.metaKey) {
                if (!e.shiftKey) document.body.classList.contains('omni-search-active') ? this.hide() : this.show();
            }

            // If the meta key, shift key and k are pressed
            if (e.key === 'k' && e.metaKey && e.shiftKey) {
                if (this.contextual_search_available && document.body.classList.contains('omni-search-active')) {
                    this.enterContextualSearch();
                }
            }

        });

        // Keydown event listeners
        document.addEventListener('keyup', (e) => {

            if (this.contextual_search_active) {
                this.filterContextualData();
                this.storeLatestSearch();
            } else {
                // Test to see if there is a contextual search available
                this.checkForContextualSearch();
            }

        });

        // If the URL contains the omni search string
        if (window.location.href.includes('omni')) this.show();

        // on click of .omni-results but not .omni-results__container
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.frame__search__list') && !e.target.closest('.frame__search')) {
                if (!e.target.closest('.gallery-modal')) {
                    this.hide();
                }
            }
        });

        this.element.addEventListener('click', (e) => {
            if (e.target.classList.contains('js-ai-toggle')) {
                this.element.classList.toggle('ai-more-shown');
                localStorage.setItem('ai-more-shown', this.element.classList.contains('ai-more-shown'));
            }
            if (e.target.classList.contains('js-images-toggle')) {
                this.element.classList.toggle('images-more-shown');
                this.show_more_images = this.element.classList.contains('images-more-shown');
                this.handleImages();
                this.results.querySelector('.frame__search__scroll').scrollTo(0, 0);
            }
        });

        this.fetchFavouriteImages();

        this.fetchImages();

        this.fetchTopics();

        this.fetchSynonyms();

        this.fetchImageSynonyms();

        this.fetchBoostings();

        this.changeTabs();

    }

    /**
     * 
     * Single image template
     * 
     * @param {Node} image 
     * @returns {String} template
     */
    image_template(image) {

        let hearted = '';

        if (this.favourite_images) {
            let keep = this.favourite_images.find(keep => parseInt(keep.entity_id) === parseInt(image.o) && keep.entity_type === 'image');
            if (keep) hearted = 'hearted';
        }

        let image_source = (image.g?.includes('http')) ? `<a href="${image.g}">${image.g}</p>` : image.g;

        return /*html*/`
        <div class="gallery-modal__wrap__images-alt__item">
            <div href="/imagedetail/${image.p}" target="_blank" class="gallery-modal__wrap__images-alt__item__image">
                <img zoomable draggable="false" class="[ js-gallery-image ]" src="${image.l}" width="640" height="480" loading="lazy" alt="${image.a}">
            </div>
            <div class="images__wrap__grid__item__heart [ js-toggle-image-favourite-alt ] ${hearted}" data-entity-id="${image.o}">
                <span class="heart">
                    <img class="heart__image-1" src="/_resources/themes/mercury/dist/images/svg/heart-red.svg" width="24" height="24" loading="lazy" alt="brown-heart">
                    <img class="heart__image-2" src="/_resources/themes/mercury/dist/images/svg/heart-full-red.svg" width="24" height="24" loading="lazy" alt="brown-heart">
                    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" stroke="#ff5050" fill="none" fill-rule="evenodd" />
                    </svg>
                </span>
            </div>
            <div class="gallery-modal__wrap__images-alt__item__caption">
                <div class="gallery-modal__wrap__images-alt__item__caption__text">
                    <h3 class="colour--secondary"><b>${image.a}</b></h3>
                    <p class="no-margin colour--secondary ">${image.b || ''} ${(image.i) ? `<span style="opacity:0.5;display:none">${image.i}</span>` : ``}</p>
                </div>
                <div class="gallery-modal__wrap__images-alt__item__caption__footer">
                    <p class="no-margin colour--secondary smaller colour--grey-mid-dark">Source: ${(image_source || '')}</p>
                    <p class="no-margin colour--secondary smaller">
                        <a href="/terms" class="colour--grey-mid-dark">Terms and conditions</a>
                        <span class="colour--grey-mid-dark">&nbsp;|&nbsp;</span>
                        <a href="#" class="colour--primary" onclick="event.preventDefault();document.body.classList.toggle('copyright-visible')">Reusing this image</a>
                    </p>
                </div>
                
            </div>
            <div class="gallery-modal__wrap__images-alt__item__copyright">
                <div class="gallery-modal__wrap__images-alt__item__copyright__close" onclick="document.body.classList.remove('copyright-visible')">
                    <img src="_resources/themes/mercury/dist/images/svg/close.svg" width="10" height="10" loading="lazy" alt="close">
                </div>
                <p class="colour--white no-margin"><b>© DermNet.</b></p>
                <p class="colour--white smaller no-margin">You can use or share this image if you comply with our image <a href="/image-licence">licence</a>. Please provide a link back to this page. 
                <br><br>For a high resolution, unwatermarked copy contact us <a href="/permission/?image=${image.o}">here</a>. Fees apply.</p>
            </div>
        </div>`;
    }


    /**
    * 
    * Navigate to the previous image
    * 
    */
    previousImage() {

        // Remove the active class from the copyright element
        document.body.classList.remove('copyright-visible');

        // Update the current index
        (this.current_index <= 0) ? this.current_index = 0 : this.current_index--;

        // Update the modal
        document.querySelector('.gallery-modal__wrap__images-alt').innerHTML = this.image_template([...this.modal_images][this.current_index]);

        console.log('ZOOM PICK - 91=8');
        if (this.zoomPick) this.zoomPick.destroy();
        this.zoomPick = new PinchZoom(document.querySelector('.gallery-modal__wrap__images-alt__item__image [zoomable]'));

    }

    /**
    * 
    * Navigate to the next image
    * 
    */
    nextImage() {

        // Remove the active class from the copyright element
        document.body.classList.remove('copyright-visible');

        // Update the current index
        (this.current_index === [...this.modal_images].length - 1) ? this.current_index = 0 : this.current_index++;

        // Update the modal
        document.querySelector('.gallery-modal__wrap__images-alt').innerHTML = this.image_template([...this.modal_images][this.current_index]);

        console.log('ZOOM PICK - 91=8');
        if (this.zoomPick) this.zoomPick.destroy();
        this.zoomPick = new PinchZoom(document.querySelector('.gallery-modal__wrap__images-alt__item__image [zoomable]'));

    }



    /**
     * Generates the HTML template for a modal gallery.
     *
     * @param {number} index - The index of the current image to display in the modal.
     * @returns {string} The HTML string for the modal gallery.
     */
    templateModal(index) {
        this.current_index = index;

        return /*html*/` 
        <div class="gallery-modal gallery-modal-alt">
            <div class="gallery-modal__wrap">
                <div class="gallery-modal__wrap__close" onclick="document.body.classList.remove('gallery-modal-open');document.body.classList.remove('copyright-visible');window.resetZoom();">
                    <img src="_resources/themes/mercury/dist/images/svg/close-dark-blue.svg" width="39px" height="40px" loading="lazy" alt="close">
                </div>
                <div class="gallery-modal__wrap__images-alt">
                    ${this.image_template(this.modal_images[this.current_index])}
                </div>
                ${(this.modal_images.length > 1) ? /*html*/`
                <button class="gallery-modal__wrap__previous-alt">
                    <img src="_resources/themes/mercury/dist/images/svg/arrow-left-medium-dark-blue.svg" width="39px" height="40px" loading="lazy" alt="Paginate to the previous image">
                </button>
                <button class="gallery-modal__wrap__next-alt">
                    <img src="_resources/themes/mercury/dist/images/svg/arrow-right-medium-dark-blue.svg" width="39px" height="40px" loading="lazy" alt="Pagniate to the next image">
                </button>`
                : ``}
            </div>
        </div>`;
    }


    /**
     * Displays the images modal by adding the 'gallery-modal-open' class to the body element
     * and setting the visibility state to true.
     */
    showImagesModal() {

        // Add the active class to the page
        document.body.classList.add('gallery-modal-open');

        // Set the visiblity state
        this.visible = true;

        console.log('ZOOM PICK - 91=8');
        if (this.zoomPick) this.zoomPick.destroy();
        this.zoomPick = new PinchZoom(document.querySelector('.gallery-modal__wrap__images-alt__item__image [zoomable]'));

    }


    /**
    * 
    * Remove any pre existing gallery modals
    * 
    */
    clean() {
        document.querySelector('.gallery-modal') ? document.querySelector('.gallery-modal').remove() : null;
    }


    /**
    *
    * Show the suggestion box
    *
    */
    focus() {
        // Add the focused class to the input
        this.input.classList.add('focused');

        // Scroll to the top of the results to avoid missing context
        if (document.querySelector('.omni-snippets-present') === null) {
            this.results.querySelector('.frame__search__scroll').scrollTo(0, 0);
            setTimeout(() => this.results.querySelector('.frame__search__scroll').scrollTo(0, 0), 100);
        }
    }


    /**
    *
    * Show the suggestion box
    *
    */
    blur() {
        this.input.classList.remove('focused');
    }


    /**
     * Removes ingress stop words from the given query string.
     *
     * @param {string} query - The query string to process.
     * @returns {string} - The query string with ingress stop words removed.
     */
    removeIngressStopWords(query) {
        return query.split(' ').filter(word => !this.ingress_stop_words.includes(word.toLowerCase())).join(' ');
    }


    /**
     *
     * Show the search box
     *
     */
    show() {

        // Add the active class to the body
        document.body.classList.add('omni-search-active');

        // Focus input
        this.input.focus();

        // Place the caret at the end of the input
        var value = this.input.value;
        this.input.value = '';
        this.input.value = value;

        if (!this.loaded_latest_search && this.retrieveLastSearch()) {
            console.log('LOADING LAST SEARCH');
            this.loaded_latest_search = true;
            this.loading_last_search = true;
            this.stored_ai_search_results = this.retrieveLastSearch().ai_search_results;
            this.stored_search_results = this.retrieveLastSearch().search_results;
            this.stored_specific_search_results = this.retrieveLastSearch().specific_search_results;
            this.handleData(this.stored_search_results);
            this.handleSpecificData(this.stored_specific_search_results);
            this.handleBotData(this.stored_ai_search_results, true);

            setTimeout(() => this.loading_last_search = false, 100);


            // Scroll to the last scroll position from a previous interaction
            setTimeout(() => {
                let last_scroll_position = localStorage.getItem('omni-searchscroll-position');
                this.results.querySelector('.frame__search__scroll').scrollTo(0, last_scroll_position);
            }, 10);


            if (this.retrieveLastSearch().contextual_search_active) {
                console.log('LOADING CONTEXTUAL SEARCH');
                this.contextual_search_url = this.retrieveLastSearch().contextual_search_url;
                this.contextual_search_topic = this.retrieveLastSearch().contextual_search_topic;
                this.contextual_search_active = this.retrieveLastSearch().contextual_search_active;
                this.contextual_search_data = this.retrieveLastSearch().contextual_search_data;
                this.contextual_search_page_title = this.retrieveLastSearch().contextual_search_page_title;
                this.enterContextualSearch();
                this.checkForContextualSearch();
            }


        }

        this.focus();
    }


    /**
     *
     * Hide the search box
     *
     */
    hide() {
        document.body.classList.remove('omni-search-active');
        this.blur();
    }


    /**
     * Constructs a URL with the given base and query parameters.
     *
     * @param {string} base - The base URL.
     * @param {string} [query=this.query] - The search query string.
     * @param {string} [type='h'] - The type parameter to be added to the URL.
     * @param {boolean} [grouped=false] - Whether to include the grouped parameter in the URL.
     * @param {boolean|Object} [filters=false] - Filters to be applied to the search query. If truthy, it will be stringified and added to the URL.
     * @returns {URL} - The constructed URL with the specified parameters.
     */
    getURL(base, query = this.query, type = 'h', grouped = false, filters = false) {
        const url = new URL(base);
        url.searchParams.set('q', (filters) ? this.removeIngressStopWords(query) : query);
        url.searchParams.set('t', type);
        if (filters) url.searchParams.set('filters', JSON.stringify(filters));
        if (grouped) url.searchParams.set('grouped', grouped);
        return url;
    }


    /**
     * 
     * Individual highlights
     * 
     * @param {object} highlights 
     * @returns 
     */
    handleHighlights(highlights, index, page_title) {
        this.highlight_links[index] = [];
        return highlights.slice(0, 2).map(highlight => {
            this.highlight_links[index].push(highlight.link);
            return /*html*/`
            <a href="${this.sanitise(highlight.link)}" data-fragment="${highlight.link}" class="frame__search__snippet" data-index="${index}">
                ${this.showEmTagsForAllowedWords(this.removeEmTagsFromStopWords(highlight.content), this.query.split(/[\s-]+/))}
                <i>${page_title}</i>
            </a><br>`;
        }).join('');
    }

    /**
     * 
     * Individual highlights
     * 
     * @param {object} highlights 
     * @returns 
     */
    handleSpecificHighlights(highlights) {
        return highlights.slice(0, 999).map(highlight => {
            return /*html*/`
            <a href="${this.sanitise(highlight.link)}" data-fragment="${highlight.link}" class="frame__search__snippet" style="white-space:pre-line;width: 100%;">${this.showEmTagsForAllowedWords(this.removeEmTagsFromStopWords(highlight.original_content), this.query.split(/[\s-]+/))}</a><br>`;
        }).join('');
    }




    /**
     * Updates the highlighted search words within the search results.
     * 
     * This function searches for words within the search results that match the 
     * user's query and wraps them in <em> tags to highlight them. It first collects 
     * all the words from the search results that are not hidden, then iterates 
     * through the snippets of text in the results, removing any existing <em> tags 
     * and re-adding them around the words that match the query.
     */
    updateHighlightedSearchWords() {

        let $words = this.results.querySelectorAll('.frame__search__word:not(.hidden)');

        let words = [];
        $words.forEach(word => {
            words.push(word.textContent.toLowerCase());
        });

        let highlights = this.results.querySelectorAll('.frame__search__snippet');

        highlights.forEach(highlight => {

            // remove all em tags
            let content = highlight.innerHTML.replace(/<em>/g, '').replace(/<\/em>/g, '');

            // split the content into words
            let content_words = content.split(/\s+/);
            // loop through the words

            content_words.forEach((content_word, index) => {
                // if the word is in the query

                if (words.includes(content_word.toLowerCase())) {
                    // add the em tags
                    content_words[index] = `<em>${content_word}</em>`;
                }
            });

            // join the words back together
            highlight.innerHTML = content_words.join(' ');

        });

    }

    /**
     * Generates HTML for displaying search words with the ability to show/hide them.
     *
     * @param {string} query - The search query containing words to be displayed.
     * @returns {string} - The generated HTML string with search words wrapped in span elements.
     */
    selectableSearchWords(query) {
        let html = '<div class="frame__search__words">Click to show/hide search words: ';
        html += query.split(' ').map(word => {
            return /*html*/`<span class="frame__search__word [ js-show-hide-search-words ]">${word}</span>`;
        }).join(' ');
        html += '</div>';
        return html;
    }

    /**
     * Handles the suggestion display for the omni-search component.
     *
     * @param {Object} suggest - The suggestion object.
     * @param {string} suggest.text - The text of the suggestion.
     * @param {string} suggest.highlight - The highlighted portion of the suggestion text.
     */
    handleSuggest(suggest) {
        if (suggest) {
            this.results.querySelector('.js-omni-suggest').innerHTML = `Did you mean <span class="[ js-omni-search-suggestion ]" data-query="${suggest.text}">${suggest.highlight}</span>`;
            document.body.classList.add('omni-suggest-present');
        }
    }


    /**
     * 
     * 
     * 
     * @param {object} data 
     */
    handleTopics(results) {
        this.highlight_links = [];

        this.topic_search_results = results.map(result => {
            return {
                score: result.score,
                url: result.record.url,
                title: result.record.title,
                highlights: this.showEmTagsForAllowedWords(this.removeEmTagsFromStopWords(result.highlight?.content || ''), this.query.split(' '))
            }
        });

        document.body.classList.remove('omni-snippets-present');

        this.results.querySelector('.js-omni-search-snippets').innerHTML = '';

        if (results && results.length) {
            let snippets = ``;
            let html = '';
            [...Array(6).keys()].forEach(i => {
                if (results[i]) html += /*html*/`<a href="${this.sanitise(results[i].record.url)}" class="frame__search__link">${results[i].record.title}</a>`
                if (results[i] && results[i].highlight && results[i].highlight.length) {
                    snippets += /*html*/`
                    <div class="frame__search__snippets" data-snippet-page-title="${results[i].record.title}">
                        <a href="${this.sanitise(results[i].record.url)}" class="frame__search__page-title">
                            <span>${results[i].record.title} <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#000000"><path d="M630.46-443H219.05q-16.09 0-27.57-11.41Q180-465.81 180-481.79q0-15.98 11.48-27.59Q202.96-521 219.05-521h411.41l-76.69-76.69q-11.92-11.93-12.81-27.89-.88-15.96 11.33-28.27 12.79-13.3 28.86-12.11 16.08 1.19 28.39 13.5l139.48 139.49q6.29 6.28 9.59 13.81 3.31 7.53 3.31 16.96t-3.31 16.96q-3.3 7.53-9.92 14.55L608.54-310.54q-11.93 11.92-27.19 12.31-15.27.38-28.06-12.92-12.21-12.31-11.52-28.08.69-15.77 13-28.08L630.46-443Z"/></svg></span>
                        </a>
                        ${this.handleHighlights(results[i].highlight, i, results[i].record.title)}
                    </div>`;
                    document.body.classList.add('omni-snippets-present');
                }
            });

            // snippets = this.selectableSearchWords(this.query) + snippets;

            this.results.querySelector('.js-omni-topics').innerHTML = html;
            this.results.querySelector('.js-omni-search-snippets').innerHTML = snippets;
        }

    }

    /**
     * Handles the display of specific topics in the search results.
     * 
     * This function processes the search results and updates the DOM to display
     * specific topic snippets if they are present. It also manages the CSS classes
     * to indicate the presence of specific search snippets.
     * 
     * @param {Array} results - The search results array. Each result is expected to have a `highlight` property.
     */
    handleSpecificTopics(results) {

        document.body.classList.remove('omni-specific-search-busy');

        this.results.querySelector('.js-omni-search-snippets-specific').innerHTML = '';

        if (results[0]) {
            let snippets = '';
            if (results[0] && results[0].highlight && results[0].highlight.length) {
                snippets += /*html*/`
                    <div class="frame__search__snippets" style="margin-top: 0rem;">
                        ${this.handleSpecificHighlights(results[0].highlight)}
                    </div>`;
                document.body.classList.add('omni-snippets-specific-present');
            }

            this.results.querySelector('.js-omni-search-snippets-specific').innerHTML = snippets;
        }

    }

    /**
     * Filters out <em> tags from a string unless the enclosed word is in the allowedWords list.
     *
     * @param {string} str - The input string containing <em> tags.
     * @param {string[]} allowedWords - An array of words that are allowed to remain within <em> tags.
     * @returns {string} - The modified string with <em> tags removed for words not in the allowedWords list.
     */
    showEmTagsForAllowedWords(str, allowedWords) {
        const regex = new RegExp(`(<em>([\\w\\s-]*?)<\/em>)`, 'gi');
        return str.replace(regex, (match, p1, p2) => {
            if (allowedWords.includes(p2.toLowerCase())) {
                return match;
            } else {
                return p2;
            }
        });
    }


    /**
     * Removes <em> tags from the stop words in the given string.
     *
     * @param {string} str - The input string from which <em> tags should be removed.
     * @returns {string} - The modified string with <em> tags removed from stop words.
     */
    removeEmTagsFromStopWords(str) {
        const regex = new RegExp(`(<em>(${this.egress_stop_words.join('|')})<\/em>)`, 'gi');
        return str.replace(regex, '');
    }


    /**
     * 
     * Clean up a URL for global usage
     * 
     * @param {string} url 
     * @returns 
     */
    sanitise(url) {
        if (!url) return '';
        return url
            .replace('https://pro.dermnetnz.org/', window.location.origin + '/')
            .replace('https://prostaging.dermnetnz.org/', window.location.origin + '/')
            .replace('?stage=Stage', '')
            .replace('#:~:text=', '?snippet=');
    }

    /**
     * 
     * 
     * 
     * @param {object} data 
     */
    handleImages(html = '') {

        console.log(`%c Start of new image search for: ${this.query}`, 'color: #94e2d5');

        if (!this.images) this.fetchImages(true);

        if (!this.images) return;

        // Create a search function
        this.image_search = createFuzzySearch(this.images, { getText: (item) => [item.a, item.b, item.c, item.i] });

        document.body.classList.remove('omni-images-present');

        let lowercase_query = (this.query) ? this.query.toLowerCase() : '';

        this.topics_and_synonyms = (!this.topics_and_synonyms || this.topics_and_synonyms.error === 'Unauthorized') ? [] : this.topics.concat(this.image_synonyms);

        let topics_from_query = (!this.topics_and_synonyms || this.topics_and_synonyms.error === 'Unauthorized') ? [] : this.topics_and_synonyms
            .filter(topics_array => {
                if (!topics_array) return false;
                let regexArray = topics_array.map(topic => {
                    return new RegExp(`\\b${topic.toLowerCase().replace(/\s+/g, '\\s+')}\\b`, 'i');
                });
                return regexArray.some(regex => regex.test(lowercase_query));
            })


        // Flatten the array
        topics_from_query = topics_from_query.flat();

        if (topics_from_query.length) {
            // topics_from_query = [topics_from_query.sort((a, b) => b.length - a.length)[0]];
            // topics_from_query = [topics_from_query[0]];
            topics_from_query = topics_from_query;

            console.log(`%c ${topics_from_query.length} condition${(topics_from_query.length === 1) ? '' : 's'} found in query: ${(topics_from_query)}`, 'color: #94e2d5');
            console.log(`%c ${topics_from_query.length} condition${(topics_from_query.length === 1) ? '' : 's'} found in query with synonyms: \n [${(topics_from_query)}]`, 'color: #94e2d5');

            this.filtered_images = this.images.filter(item =>
                ['a', 'b', 'c', 'i'].some(key =>
                    item[key]?.toLowerCase() &&
                    topics_from_query.some(condition => item[key].toLowerCase().includes(condition.toLowerCase()))
                )
            );

        } else {
            console.log(`%c no conditions found in query`, 'color: #94e2d5');
            this.filtered_images = this.images.filter(item =>
                ['a', 'b', 'c', 'i'].some(key => item[key]?.toLowerCase().includes(lowercase_query))
            );

            //If we do not have any imagers present then we should search for them with a fuzzy search
            if (this.filtered_images.length === 0) {
                console.log(`%c performing a fuzzy image search`, 'color: #94e2d5');
                this.filtered_images = this.image_search(lowercase_query);
                this.filtered_images = this.filtered_images.map(item => item.item);
            }
        }


        console.log(`%c ${this.filtered_images.length} resulting images`, 'color: #94e2d5');

        // We have removed this in favour of a randomised image search
        if (this.query === '') {
            console.log(`%c Randomising images`, 'color: #94e2d5');
            this.randomiseImages();
        } else {
            if (this.query) this.sortImages((topics_from_query) ? topics_from_query[0] : undefined);
        }


        // Reset the modal images
        this.modal_images = [];

        // If we don't  have a query then show the standard images randomised
        if (this.input.value === '') {
            this.filtered_images = this.images.filter(item => this.initial_images_array.indexOf(item.o) > 0);
            this.filtered_images = this.filtered_images.sort(() => Math.random() - 0.5);
        }


        // Render out the images based on their position in the collection
        [...Array((this.show_more_images) ? 9999999 : (window.innerWidth < 768) ? 4 : 5).keys()].map((item, index) => {

            // Get the starting position of the images
            let itemIndex = index;

            // Only add it to the HTML if it exists and we haven't looped nothing
            if (this.filtered_images[itemIndex]) html += this.templateImage(this.filtered_images[itemIndex], index)

            // Add the image to the modal images
            if (this.filtered_images[itemIndex]) this.modal_images.push(this.filtered_images[itemIndex])

        });

        // // Render the HTML
        this.element.querySelector('.js-omni-images').innerHTML = (html === '') ? this.empty() : html;

        if (html === '') document.body.classList.add('omni-images-present');;

    }



    /**
     * Simulates a typewriter effect by displaying text one word at a time within a given HTML element.
     *
     * @param {HTMLElement} element - The HTML element where the text will be displayed.
     * @param {string} text - The text to be displayed with the typewriter effect.
     * @param {number} [minSpeed=this.typing_speed] - The minimum speed (in milliseconds) between each word being typed.
     * @param {number} [maxSpeed=this.typing_speed * 4] - The maximum speed (in milliseconds) between each word being typed.
     */
    typeWriter(element, text, minSpeed = this.typing_speed, maxSpeed = this.typing_speed * 4) {

        // Instantly show the text if the typing speed is set to 0
        if (this.typing_speed === 0) {
            element.textContent = text;
            return
        }

        const words = text.split(' ');
        let currentIndex = 0;

        function typeNextWord() {
            if (currentIndex < words.length) {

                // Add the next word followed by a space
                element.textContent += words[currentIndex] + " ";

                currentIndex++;

                // Calculate a random delay between minSpeed and maxSpeed
                const randomDelay = Math.floor(Math.random() * (maxSpeed - minSpeed + 1)) + minSpeed;

                setTimeout(typeNextWord, randomDelay);
            }
        }

        // Start the typing animation
        typeNextWord();
    }

    /**
     * 
     * Test to see if we have any currently active searches
     * 
     * @returns boolean
     */
    isSearchBusy() {
        return this.ai_search_active || this.search_active || this.specific_search_active || this.loading_last_search;
    }


    /**
     *  
     * Save the latest search for review
     * 
     */
    saveSearchForReview() {

        if (!this.isSearchBusy()) {
            console.log(`%c Saving search`, 'color: #5856D6');

            this.storeLatestSearch();

            fetch(`${window.location.origin}/esearch/saveAI`, {
                method: 'POST',
                body: JSON.stringify({
                    query: this.query,
                    sanitised_query: this.query,
                    search_type: this.ai_search_type,
                    ai_search_results: this.ai_search_results,
                    ai_search_time: this.ai_search_time + 'ms',
                })
            })
                .then(response => response.json())
                .then(data => {
                    this.saved_search_review_id = data.data.id;
                    console.log(`%c Saving search for review`, 'color: #5856D6')
                })
                .catch(error => console.error('Error:', error))

        } else {
            console.log(`%c Intercepted search save due to busy response`, 'color: #5856D6');
        }
    }

    AISearchFeedback(e, feedback) {
        e.preventDefault();
        console.log(`%c Flagging search for review`, 'color: #5856D6');
        if (this.saved_search_review_id) {

            // Add a busy class to the clicked button
            e.target.classList.add('busy');

            fetch(`${window.location.origin}/esearch/feedbackAI`, {
                method: 'POST',
                body: JSON.stringify({
                    flag: 'feedback',
                    feedback: feedback,
                    id: this.saved_search_review_id
                })
            })
                .then(response => response.json())
                .then(data => {

                    // Remove a busy class to the clicked button
                    setTimeout(() => e.target.classList.remove('busy'), 500);

                    console.log(`%c Gave feedback to search result`, 'color: #5856D6');

                    Toast.fire({
                        icon: 'success',
                        title: 'Thanks for your giving us your feedback'
                    });

                })
                .catch(error => {
                    // Remove a busy class to the clicked button
                    setTimeout(() => e.target.classList.remove('busy'), 500);

                    console.error('Error:', error)
                })
        } else {
            console.log(`%c No ID to give feedback on`, 'color: #5856D6');

        }
    }


    flagAISearch(e) {
        e.preventDefault();
        console.log(`%c Flagging search for review`, 'color: #5856D6');
        if (this.saved_search_review_id) {

            // Add a busy class to the clicked button
            e.target.classList.add('busy');

            fetch(`${window.location.origin}/esearch/flagAI`, {
                method: 'POST',
                body: JSON.stringify({
                    flag: 'bad',
                    id: this.saved_search_review_id
                })
            })
                .then(response => response.json())
                .then(data => {

                    // Remove a busy class to the clicked button
                    setTimeout(() => e.target.classList.remove('busy'), 500);
                    setTimeout(() => e.target.classList.add('flagged'), 500);
                    this.flagged = true;

                    console.log(`%c Flagged search for review`, 'color: #5856D6')
                })
                .catch(error => {
                    // Remove a busy class to the clicked button
                    setTimeout(() => e.target.classList.remove('busy'), 500);
                    this.flagged = false;

                    console.error('Error:', error)
                })
        } else {
            console.log(`%c No ID to flag review`, 'color: #5856D6');

        }
    }

    /**
     * Handles the bot data and updates the search results.
     *
     * @param {Object} data - The data returned from the bot.
     * @param {boolean} [instant=false] - Whether to display the results instantly or with a typing effect.
     */
    handleBotData(data, instant = false) {

        if (data && data.result) {
            this.results.querySelector('.js-ai-summary').innerHTML = '';
            this.results.querySelector('.js-ai-summary').style.whiteSpace = 'break-spaces';
            this.typeWriter(this.results.querySelector('.js-ai-summary'), data.result, instant ? 0 : this.typing_speed, instant ? 0 : this.typing_speed * 4);
            this.stored_ai_search_results = data;
            this.ai_search_results = data.result;
            this.ai_search_time = new Date().getTime() - this.search_start;

            if (data && data.documents) {
                let html = '<b>Sources:</b> ';
                data.documents.forEach((document, index) => {
                    html += /*html*/`<a href="${this.sanitise(document.source)}">${document.title}</a>`;
                    if (index < data.documents.length - 1) html += ', ';
                });
                this.results.querySelector('.js-ai-sources').innerHTML = html;
            }
        }

        this.saveSearchForReview();
    }

    /**
     * Handles the search data by storing the results, recording the search end time,
     * processing suggestions and topics, handling images, and saving the search for review.
     *
     * @param {Object} data - The search data object.
     * @param {Object} data.data - The main data object containing suggestions and results.
     * @param {Object} data.data.suggest - The suggestions data.
     * @param {Object} data.data.results - The results data.
     * @param {Array} data.data.results.topics - The array of topic results.
     */
    handleData(data) {
        this.stored_search_results = data;
        this.search_end = new Date().getTime();
        if (data && data.data && data.data.suggest) this.handleSuggest(data.data.suggest);
        if (data && data.data && data.data.results && data.data.results.topics) this.handleTopics(data.data.results.topics);
        this.handleImages();
        this.saveSearchForReview();
    }

    /**
     * Handles specific data by storing the search results and processing specific topics if available.
     *
     * @param {Object} data - The data object containing search results.
     * @param {Object} data.data - The nested data object.
     * @param {Object} data.data.results - The results object within the data.
     * @param {Array} data.data.results.topics - The array of specific topics within the results.
     */
    handleSpecificData(data) {
        this.stored_specific_search_results = data;
        if (data && data.data && data.data.results && data.data.results.topics) this.handleSpecificTopics(data.data.results.topics);
    }

    /**
     * Authenticates the AI by sending a POST request to the authentication endpoint.
     * The email is retrieved from the input field with the class 'flyout__header__email'.
     * If the authentication is successful and the number of login attempts is less than 3,
     * it proceeds to call the searchAI method.
     * 
     * @method authenticateAI
     * @returns {void}
     */
    authenticateAI() {
        const url = new URL(ENDPOINTS.AUTHENTICATE_AI_ENDPOINT);

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: document.querySelector('.flyout__header__email').value,
            })
        })
            .then(response => response.json())
            .then(() => {
                // Retry searching if the authentication is successful but limit the number of attempts to 3 to avoid an infinite loop
                if (this.login_attempts < 3) this.searchAI();
                this.login_attempts++;
            })
    }

    templateDiagnosis(item, index) {
        console.log(item);
        return /*html*/`
        <div class="frame__search__item__diagnosis">
            <div class="frame__search__item__diagnosis__content">
                <h4 class="frame__search__item__diagnosis__content__link no-margin">
                    <a href="${item.url}" class="arrow-links">${item.title}</a>
                </h4>
                ${(item.summary) ? `<p>${item.summary}</p>` : `<p>Dolore labore sunt velit do nulla consectetur nulla occaecat consectetur labore. Nulla occaecat qui labore sunt. Labore est culpa sint sit pariatur excepteur enim dolore occaecat occaecat veniam minim laboris. Incididunt culpa anim nulla dolor minim. Eiusmod non irure velit sunt do. Consectetur ut quis eu duis sunt consectetur ad anim enim pariatur nulla laborum ea. Laboris magna laboris est excepteur.`}
            </div> 
            <div class="frame__search__item__diagnosis__images">
            ${item.images.map((image, index) => {
            return /*html*/`
                    <div class="frame__search__item__diagnosis__images__image">
                        <img src="${image.url}" alt="${item.caption}" loading="lazy" />
                    </div>`;
        }).join('')}
            </div>
            <div class="frame__search__item__diagnosis__arrow">
                <a href="${item.url}"></a>
            </div>
        </div>`;
    }

    handleDiagnosisData() {
        let html = '';
        if (this.diagnosis_results && this.diagnosis_results.length) {
            this.diagnosis_results.forEach((item, index) => {
                html += this.templateDiagnosis(item, index);
            });
        } else {
            html = /*html*/``;
        }
        this.results.querySelector('.diagnosis').innerHTML = html;
    }

    searchAIDiagnosis() {


        const url = new URL(ENDPOINTS.SEARCH_AI_DIAGNOSIS_ENDPOINT);
        fetch(url, {
            method: 'POST',
            body: JSON.stringify({
                q: this.query
            }),
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then(response => response.json())
            .then(data => {
                if (data?.data?.results) {
                    this.diagnosis_results = data.data.results;
                } else {
                    this.diagnosis_results = [];
                }

                this.handleDiagnosisData();

            })
            .catch(error => (error.name === 'AbortError') ? console.log('Search aborted') : console.error('Error:', error))
            .finally(() => {

            });


    }


    /**
     * Initiates an AI-based search and updates the UI with the results.
     * 
     * This function sets the AI search state to active, updates the UI to show loading
     * skeletons, and sends a POST request to the AI search endpoint with the query and
     * search parameters. It handles the response by either displaying the results or
     * handling errors such as invalid tokens.
     * 
     * @function
     * @memberof OmniSearch
     * @returns {void}
     */
    searchAI() {

        this.saved_search_review_id = null;
        this.ai_search_active = true;

        this.results.querySelector('.js-ai-summary').style.whiteSpace = 'normal';
        this.results.querySelector('.js-ai-summary').innerHTML = /*html*/`
            <p class="skeleton" style="width:95%">&nbsp;</p>
            <p class="skeleton" style="width:55%">&nbsp;</p>
            <p class="skeleton" style="width:75%">&nbsp;</p>`;

        this.results.querySelector('.js-ai-sources').innerHTML = /*html*/`
            <p class="skeleton" style="width:55%">&nbsp;</p>`;

        document.body.classList.add('omni-search-ai-busy');

        const url = new URL(ENDPOINTS.SEARCH_AI_ENDPOINT);

        let words = 80;
        let bullet_points = false;

        // Update the searchAI type
        if (this.ai_search_type === 'short') {
            words = 10;
            bullet_points = false;
        } else if (this.ai_search_type === 'medium') {
            words = 60;
            bullet_points = false;
        } else if (this.ai_search_type === 'long') {
            words = 120;
            bullet_points = false;
        } else if (this.ai_search_type === 'bullets') {
            words = 80;
            bullet_points = true;
        }

        fetch(url, {
            method: 'POST',
            body: JSON.stringify({
                query: this.query,
                words: words,
                bullet_points: bullet_points
            }),
            headers: {
                'Content-Type': 'application/json',
                'token': Cookies.get('aist')
            }
        })
            .then(response => response.json())
            .then(data => {
                this.ai_search_active = false;
                if (data.error) {
                    document.body.classList.remove('omni-search-ai-busy');
                    if (data.error === 'invalid token') {
                        console.log(`%c AIST key is invalid or missing`, 'color: #eba1ac');

                        // Remove the AIST key in case it is invalid
                        Cookies.remove('aist');

                        // We should authenticate the user to allow them to search
                        this.authenticateAI();

                    } else {
                        this.hide();
                    }
                } else {
                    this.handleBotData(data);
                }
            })
            .catch(error => (error.name === 'AbortError') ? console.log('Search aborted') : console.error('Error:', error))
            .finally(() => {
                this.ai_search_active = false;
                document.body.classList.remove('omni-search-ai-busy');
            });
    }

    /**
     * Initiates a search operation.
     * 
     * This function performs the following steps:
     * 1. Shows the results container if it is hidden.
     * 2. Sets the search state to active.
     * 3. Updates the document body classes to indicate a search is in progress.
     * 4. Resets the search start time.
     * 5. Retrieves the query from the input field.
     * 6. Logs the search query to the console.
     * 7. If the query is empty, it stops the search and updates the document body classes.
     * 8. Displays skeleton loading placeholders in the results container.
     * 9. Adds the query to the search history and limits the history to 10 items.
     * 10. Saves and renders the search history.
     * 11. Aborts any previous search if it exists.
     * 12. Creates a new AbortController instance for the new search.
     * 13. Filters topics from the query and performs a specific search if topics are found.
     * 14. Fetches search results from the server and handles the response data.
     * 15. Initiates an AI search.
     * 16. Blurs the input field after a short delay.
     */
    search() {

        // Show the results container incase it is hidden
        this.show();

        this.search_active = true;

        this.flagged = false;
        this.element.querySelector('.js-flag-search').classList.remove('flagged');
        document.body.classList.add('omni-search-busy');
        document.body.classList.remove('omni-suggest-present');
        document.body.classList.remove('omni-snippets-specific-present');

        // Reset the searches made counter
        this.search_start = new Date().getTime();

        // Get the query from the input
        this.query = this.input.value;

        console.log(`\n%c Searching for: ${this.query}`, "color:#fff;font-size:12px;font-weight:400; padding:0.45rem 0.75rem; margin: 0rem auto 1rem auto; font-family: Metropolis, Helvetica, sans-serif; border: 2px solid #0dd8d8; border-radius: 4px;background-size: cover;background-repeat: no-repeat;border: double 1px transparent;background-image: linear-gradient(#282828, #282828), radial-gradient(circle at top left, #1C48AB, #24ACF0);background-origin: border-box;background-clip: padding-box, border-box;")

        // If there is no query, do nothing
        if (!this.query) {
            document.body.classList.remove('omni-search-busy', 'omni-specific-search-busy');
            return;
        }

        this.results.querySelector('.js-omni-search-snippets').innerHTML = /*html*/`
        <p class="skeleton" style="width:95%">&nbsp;</p>
        <p class="skeleton" style="width:55%">&nbsp;</p>
        <p class="skeleton" style="width:75%">&nbsp;</p>
        <p class="skeleton" style="width:0%">&nbsp;</p> 
        <p class="skeleton" style="width:95%">&nbsp;</p>
        <p class="skeleton" style="width:55%">&nbsp;</p>
        <p class="skeleton" style="width:75%">&nbsp;</p>`;

        this.results.querySelector('.js-omni-topics').innerHTML = /*html*/`
        <p class="skeleton" style="width:95%">&nbsp;</p>
        <p class="skeleton" style="width:55%">&nbsp;</p>
        <p class="skeleton" style="width:75%">&nbsp;</p>`;

        this.results.querySelector('.js-omni-images').innerHTML = /*html*/`
        <p class="skeleton skeleton--image">&nbsp;</p>
        <p class="skeleton skeleton--image">&nbsp;</p>
        <p class="skeleton skeleton--image">&nbsp;</p>`;

        this.checkForContextualSearch();

        // Abort the previous search if it exists
        if (this.abort_controller) this.abort_controller.abort();

        // Create a new AbortController instance for the new search
        this.abort_controller = new AbortController();

        const signal = this.abort_controller.signal;
        // let lowercase_query = this.query.toLowerCase();


        fetch(this.getURL(ENDPOINTS.SEARCH_ALL_ENDPOINT, this.query, 'h', 1), { signal })
            .then(response => response.json())
            .then(data => {

                if (data.data.error) {
                    document.body.classList.remove('omni-search-ai-busy');
                    this.hide();
                } else {
                    this.handleData(data);
                }
            })
            .catch(error => (error.name === 'AbortError') ? console.log('Search aborted') : console.error('Error:', error))
            .finally(() => {
                this.search_active = false;
                document.body.classList.remove('omni-search-busy')
            });

        this.searchAI();

        this.searchAIDiagnosis();

        this.fetchContextualContent(true);

        setTimeout(() => {
            this.input.blur();
        }, 100);

    }

    fetchFavouriteImages() {
        API.fetch_favourites({ keep_type: 'favourite' })
            .then((response) => {
                if (response && response.data) {
                    this.favourite_images = response.data;
                }
            })
    }

    /**
     * Fetches images from  storage or a remote CDN.
     * 
     * If images are found in local storage (with the key 'dermnet_images'), they are parsed and assigned to `this.images`.
     * If the `search` parameter is true, `this.handleImages()` is called after assigning the images.
     * 
     * If images are not found in local storage, a JSON file is fetched from the CDN.
     * The fetched data is stored in local storage with an expiry time and then parsed and assigned to `this.images`.
     * If the `search` parameter is true, `this.handleImages()` is called after assigning the images.
     * 
     * @param {boolean} [search=false] - Determines whether to call `this.handleImages()` after fetching images.
     */
    fetchImages(search = false) {

        if (getWithExpiry('dermnet_images')) {
            this.images = JSON.parse(getWithExpiry('dermnet_images'));
            if (search) this.handleImages();
        } else {
            // Fetch a pre cached JSON file from our CDN
            fetch('/assets/___cache/images.json?v=1.0.2')
                .then((response) => response.text())
                .then(data => {
                    setWithExpiry('dermnet_images', data);
                    this.images = JSON.parse(data);
                    if (search) this.handleImages();
                });
        }

    }




    fetchTopics() {

        if (getWithExpiry('dermnet_pro_topics')) {
            this.topics = JSON.parse(getWithExpiry('dermnet_pro_topics'));
        } else {
            // Fetch a pre cached JSON file from our CDN
            fetch('/snippetsearch/topics')
                .then((response) => response.json())
                .then(data => {
                    setWithExpiry('dermnet_pro_topics', JSON.stringify(data.data));
                    this.topics = data.data;
                });
        }

    }

    fetchSynonyms() {

        if (getWithExpiry('dermnet_pro_synonyms')) {
            this.synonyms = JSON.parse(getWithExpiry('dermnet_pro_synonyms'));
        } else {
            // Fetch a pre cached JSON file from our CDN
            fetch('/snippetsearch/synonyms')
                .then((response) => response.json())
                .then(data => {
                    setWithExpiry('dermnet_pro_synonyms', JSON.stringify(data.data));
                    this.synonyms = data.data;
                });
        }

    }

    fetchImageSynonyms() {

        if (getWithExpiry('dermnet_pro_image_synonyms')) {
            this.image_synonyms = JSON.parse(getWithExpiry('dermnet_pro_image_synonyms'));
        } else {
            // Fetch a pre cached JSON file from our CDN
            fetch('/snippetsearch/image_synonyms')
                .then((response) => response.json())
                .then(data => {
                    setWithExpiry('dermnet_pro_image_synonyms', JSON.stringify(data.data));
                    this.image_synonyms = data.data;
                });
        }

    }

    fetchBoostings() {

        if (getWithExpiry('dermnet_pro_boostings')) {
            this.boostings = JSON.parse(getWithExpiry('dermnet_pro_boostings'));
        } else {
            // Fetch a pre cached JSON file from our CDN
            fetch('/snippetsearch/boostings')
                .then((response) => response.json())
                .then(data => {
                    setWithExpiry('dermnet_pro_boostings', JSON.stringify(data.data));
                    this.boostings = data.data;
                });
        }

    }

    /**
     * Randomises the order of images in the `filtered_images` array.
     * Logs a message to the console indicating that the images are being randomised.
     */
    randomiseImages() {
        console.log(`%c Randomising images 🎲`, 'color: #94e2d5');
        this.filtered_images.sort(() => Math.random() - 0.5);
    }

    /**
     * Sorts the filtered images based on a boost score calculated from various conditions.
     * 
     * The boost score is determined by checking if certain properties of the image
     * (title, subject code, headline, keyword) match the query or an optional condition.
     * 
     * Boost scoring:
     * - Title match: +100
     * - Subject code match: +75
     * - Headline match: +50
     * - Keyword match: +25
     * 
     * The images are then sorted in descending order based on their boost score.
     * 
     * @param {string} [condition] - An optional condition to use for matching instead of the query.
     */
    sortImages(condition = undefined) {

        console.log(`%c Sorting images 🃏`, 'color: #94e2d5');

        this.filtered_images.forEach((item) => {
            // Set the boost to 0
            item.boost = 0;

            // If the image has a title that matches the query, then we should boost it
            if (item.a?.toLowerCase().includes((condition) ? condition : this.query.toLowerCase())) item.boost += 100;

            // This has been added by me to give a higher priority to the title if hte query owns it
            if (item.a?.toLowerCase().includes(this.query.toLowerCase())) item.boost += 100;

            // If the image has a subject code that matches the query, then we should boost it
            if (item.i?.toLowerCase().includes((condition) ? condition : this.query.toLowerCase())) item.boost += 75;

            // If the image has a headline that matches the query, then we should boost it
            if (item.b?.toLowerCase().includes((condition) ? condition : this.query.toLowerCase())) item.boost += 50;

            // If the image has a keyword that matches the query, then we should boost it
            if (item.c?.toLowerCase().includes((condition) ? condition : this.query.toLowerCase())) item.boost += 25;

        });

        // Sort the images based on their boost
        this.filtered_images.sort((a, b) => b.boost - a.boost);

    }

    /**
     * Generates an HTML template string for an image item.
     *
     * @param {Object} item - The image item object.
     * @param {string} item.o - The unique identifier for the image.
     * @param {string} item.c - The keywords associated with the image.
     * @param {string} item.l - The URL of the image.
     * @param {string} item.a - The alt text for the image.
     * @param {string} item.b - The headline or title for the image.
     * @param {boolean} item.e - Flag indicating if the image is sensitive.
     * @param {string} item.f - The copyright information for the image.
     * @param {string} item.h - The terms associated with the image.
     * @param {string} item.i - Additional query information for the image.
     * @param {number} index - The index of the image in the list.
     * @returns {string} The HTML template string for the image item.
     */
    templateImage(item, index) {
        return /*html*/`
        <div class="images__wrap__grid__item" data-id="${item.o}" data-keywords="${item.c}" data-index="${index}">
            <div class="gallery__slides__item__image">
                <div class="load"></div>
                <img src="${item.l}" class="[ js-omni-image ]${(item.e) ? `[ xsensitive-imagex ]` : ``}" width="640" height="480" loading="lazy" alt="${item.a}" onerror="this.classList.add('error')" data-title="${item.a}" data-headline="${item.b}" data-copyright="${item.f}" data-terms="${item.h}">
            </div> 
            <div class="gallery__slides__item__caption">
                <p class="smaller no-margin colour--secondary">${item.b || item.a}${(item.i) ? `, <a href="/images?query='${item.i}'" style="opacity:0.5;">(${item.i})</a>` : ``}</p>
            </div>
        </div>`;
    }

    /**
     * Generates an HTML string indicating that no images were found.
     *
     * @returns {string} The HTML string to display when no images are found.
     */
    empty() {
        return /*html*/`
        <div class="topics__wrap__grid__column topics__wrap__grid__column--full">
            <div class="topics__wrap__grid__column__heading">
                <h3 class="h4 no-margin">No images found</h3>
            </div>
        </div>`;
    }

    checkForContextualSearch() {
        this.contextual_search_topic = undefined;
        let lowercase_query = this.input.value.toLowerCase();

        let topics_from_query = (!this.topics || this.topics.error === 'Unauthorized') ? [] : this.topics
            .filter(topics_array => {
                if (!topics_array) return false;
                let regexArray = topics_array.map(topic => {
                    return new RegExp(`\\b${topic.toLowerCase().replace(/\s+/g, '\\s+')}\\b`, 'i');
                });
                return regexArray.some(regex => regex.test(lowercase_query));
            });

        // Find the longest first item in the array of arrays and select that array
        topics_from_query = topics_from_query.sort((a, b) => b[0].length - a[0].length);
        topics_from_query = topics_from_query[0];

        let topic_from_query = topics_from_query?.[0] || undefined;

        if (topic_from_query) {
            console.log(`%c Contextual search topic found: ${topic_from_query}`, 'color: #00D90A');
            this.contextual_search_available = true;
            this.contextual_search_topic = topic_from_query;
            this.element.querySelector('.js-query-text').innerHTML = `Checking DermNet <br>for your <b>${this.contextual_search_topic}</b> query`;
        } else {
            this.contextual_search_available = false;
            this.contextual_search_topic = undefined;
            this.contextual_search_data = undefined;
            this.contextual_search_page_title = undefined;
            this.has_fetched_contextual_data = false;
            this.element.querySelector('.js-query-text').innerHTML = `Checking DermNet <br>for your query`;

        }

        this.toggleContextualSearch();

    }

    toggleContextualSearch() {
        if (!this.contextual_search_topic) {
            document.body.classList.remove('omni-contextual-search-present');
            this.has_fetched_contextual_data = false;
            this.contextual_search_data = undefined;
            this.contextual_search_page_title = undefined;
        } else {
            this.element.querySelector('.js-enter-contextual-search i').textContent = `Search within '${this.contextual_search_topic}'`;
            document.body.classList.add('omni-contextual-search-present');

            if (!this.has_fetched_contextual_data) {
                this.fetchContextualContent();
                this.has_fetched_contextual_data = true;
            } else {
                this.filterContextualData();
            }
        }
    }

    enterContextualSearch() {
        this.contextual_search_active = true;
        console.log(`%c Entering contextual search for: ${this.contextual_search_topic}`, 'color: #FAB387');
        document.body.classList.add('omni-contextual-search-active');
        this.fetchContextualContent();
    }

    exitContextualSearch() {
        this.contextual_search_active = false;
        console.log(`%c Exiting contextual search`, 'color: #FAB387');
        document.body.classList.remove('omni-contextual-search-active');
        this.contextual_search_data = undefined;
        this.contextual_search_page_title = undefined;
        this.checkForContextualSearch();
    }

    fetchContextualContent(force = false) {

        if (force && this.contextual_search_topic || !this.contextual_search_data && this.contextual_search_topic) {
            console.log(`%c Fetching contextual content for: ${this.contextual_search_topic}`, 'color: #FAB387');

            const url = new URL(ENDPOINTS.FETCH_CONTEXTUAL_CONTENT);
            url.searchParams.set('title', this.contextual_search_topic);

            fetch(url)
                .then(response => response.json())
                .then(data => {
                    this.contextual_search_url = data.data[0]?.url;
                    this.contextual_search_data = data.data[0]?.content_snippets;
                    this.contextual_search_page_title = data.data[0]?.title;
                    this.handleContextual(this.contextual_search_url, this.contextual_search_data, this.contextual_search_page_title);
                })
                .catch(error => (error.name === 'AbortError') ? console.log('Search aborted') : console.error('Error:', error))

        } else {
            this.handleContextual(this.contextual_search_url, this.contextual_search_data, this.contextual_search_page_title);
        }

    }

    handleContextual(data) {
        console.log(`%c Handling contextual content`, 'color: #FAB387');
        if (!this.contextual_search_data) return;
        this.storeLatestSearch();
        this.filterContextualData();
    }

    filterContextualSnippets(snippets, lowercase_query) {

        if (!snippets) return [];

        let words = lowercase_query.split(' ');

        words = words.filter(word => !window.contextual_stop_words.includes(word));

        let words_and_synonyms = [];

        // Find synonyms for the words in the query
        words.forEach(word => {
            let synonyms = this.synonyms.filter(synonyms_array => {
                if (!synonyms_array) return false;
                let synonyms_array_lower = synonyms_array.map(synonym => synonym.toLowerCase());
                return synonyms_array_lower.includes(word);
            });

            if (synonyms.length) {
                synonyms.forEach(synonym => {
                    words_and_synonyms.push(synonym);
                })
            }

            words_and_synonyms.push([word]);

        });

        // Unique the words and synonyms
        words_and_synonyms = words_and_synonyms.flat().filter((item, index, self) => self.indexOf(item) === index);
        //remove any empty strings
        words_and_synonyms = words_and_synonyms.filter(word => word.length > 0);

        // order words_and_synonyms by length
        words_and_synonyms = words_and_synonyms.sort((a, b) => b.length - a.length);


        console.log(`%c words_and_synonyms: ${words_and_synonyms}`, 'color: #6BD7CA');

        let matched_snippets = [];

        snippets.map(snippet => {

            let matched_snippet = {
                item: snippet,
                score: 0,
                matches: []
            };

            let matches = [];

            words_and_synonyms.forEach(word => {
                if (word.length === 0) return;
                // search for the word in the snippet and if found add the match to the matches array [start_index, end_index]
                let regex = new RegExp(`\\b${word.replace(/\s+/g, '\\s+')}\\b`, 'i');

                let match = regex.exec(snippet);

                if (match) {
                    // matches.push([match.index, match.index + match[0].length]);

                    let newMatch = [match.index, match.index + match[0].length];

                    // Check if the new match overlaps with any existing match
                    let hasOverlap = matches.some(existingMatch =>
                        !(newMatch[1] <= existingMatch[0] || newMatch[0] >= existingMatch[1])
                    );

                    if (!hasOverlap) {
                        matches.push(newMatch);
                    }
                }
            });

            matched_snippet.matches = [matches];

            matched_snippet.score = matches.reduce((score, match) => {
                const [start, end] = match;
                const matchLength = end - start;
                const matchedWord = snippet.substring(start, end).toLowerCase();

                // Check if the matched word is a boosting word and apply the boost
                const boostValue = this.boostings.reduce((boost, { word, boost: boostAmount }) =>
                    word.toLowerCase() === matchedWord ? boost + boostAmount : boost, 0
                );

                return score + matchLength + boostValue;
            }, 0);

            if (matched_snippet.matches[0].length) matched_snippets.push(matched_snippet);

        });


        matched_snippets = matched_snippets.sort((a, b) => b.score - a.score);
        return matched_snippets;

    }

    filterContextualData() {

        console.log(`%c Filtering contextual content`, 'color: #FAB387');

        let lowercase_query = this.input.value.toLowerCase();

        let topics_from_query = (!this.topics || this.topics.error === 'Unauthorized') ? [] : this.topics
            .filter(topics_array => {
                if (!topics_array) return false;

                let regexArray = topics_array.map(topic => {
                    let sanitizedTopic = topic.toLowerCase()
                        .replace(/[\s,/]+/g, '\\s*'); // Replace spaces, commas, and slashes with `\s*`

                    return new RegExp(`\\b${sanitizedTopic}\\b`, 'i');
                });

                let sanitizedQuery = lowercase_query.replace(/[\s,/]+/g, ' '); // Normalize query

                return regexArray.some(regex => regex.test(sanitizedQuery));

            });

        // Find the longest first item in the array of arrays and select that array
        topics_from_query = topics_from_query.sort((a, b) => b[0].length - a[0].length);
        topics_from_query = topics_from_query[0];

        topics_from_query.forEach(synonym => {
            if (lowercase_query.includes(synonym.toLowerCase())) {
                console.log(`%c Found synonym: ${synonym}`, 'color: #00D90A');
                lowercase_query = lowercase_query.replace(synonym.toLowerCase(), '');
            }
        });

        console.log(`%c Lowercase query: ${lowercase_query} `, 'color: #6BD7CA');

        this.filtered_contextual_snippets = this.filterContextualSnippets(this.contextual_search_data, lowercase_query);

        let snippets = '';

        let snippet_count = 0;

        if (this.filtered_contextual_snippets.length) {
            this.filtered_contextual_snippets.forEach((snippet, index) => {
                if (snippet_count > 8) return;
                snippets += /*html*/`
                <a href="${window.location.origin}${this.contextual_search_url.toLowerCase().replace(/ /g, '-').replace(/'/g, '')}?snippet=${this.fragmentCreator(snippet.item)}" class="frame__search__snippet xxx" style="padding:1rem" >
                    ${this.markedContextualSnippet(snippet.item, snippet.matches[0])}
                </a> <br>`;
                snippet_count++;
            });

        }

        this.results.querySelector('.js-omni-search-snippets-contextual').innerHTML = (snippets === '') ? `<p class="frame__search__snippet">No snippets found</p>` : snippets;


        const snippetElement = this.element.querySelector('.frame__search__item--snippets-contextual');
        if (snippetElement) {
            snippetElement.style.position = (snippets === '') ? 'absolute' : 'relative';
            snippetElement.style.visibility = (snippets === '') ? 'hidden' : 'visible';
            snippetElement.style.left = (snippets === '') ? -500000 : 0;
        }


        if (this.contextual_search_page_title) {
            this.results.querySelector('.js-contextual-snippets-title').innerHTML = `${this.contextual_search_page_title.charAt(0).toUpperCase() + this.contextual_search_page_title.slice(1)} topic page`;
        }

        this.contextual_search_active = true;

        let same_page_snippets = document.querySelectorAll(`[data-snippet-page-title="${this.contextual_search_page_title}"]`)

        // Show or hide the snippets based on the query and if they are duplicating data
        same_page_snippets.forEach(snippet => {
            snippet.style.display = (snippets === '') ? 'block' : 'none';
        });
    }

    markedContextualSnippet(snippet, matches_array) {

        // Replace borken emdashes
        snippet = snippet.replace(/â/g, "—");
        snippet = snippet.replace(/â/g, "—");
        snippet = snippet.replace(/â/g, "'");
        snippet = snippet.replace(/â¤/g, "—");
        snippet = snippet.replace(/â¥/g, "—");
        snippet = snippet.replace(/â/g, "'");
        snippet = snippet.replace(/Î²/g, "β");

        // Order the matches array by the first item in each array
        matches_array = matches_array.sort((a, b) => a[0] - b[0]);

        let marked = '';
        let last_end = 0;

        matches_array.forEach((match, index) => {
            marked += snippet.substring(last_end, match[0]);
            marked += `<em>${snippet.substring(match[0], match[1])}</em>`;
            last_end = match[1];
        });

        marked += snippet.substring(last_end);


        return marked;

    }

    fragmentCreator(snippet) {
        const encodeFragment = str =>
            encodeURIComponent(str)
                .replace(/[!'()*,]/g, c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);

        let words = snippet.split(' ');

        let first_words = words.length > 8 ? words.slice(0, 4).join(' ') : words.slice(0, words.length).join(' ');
        let last_words = words.length > 8 ? words.slice(-4).join(' ') : '';

        // If the first_words contain a coma, we should remove it and everything after it
        if (first_words.includes(',')) {
            first_words = first_words.split(',')[0];
        }

        // If the last_words contain a coma, we should remove it and everything before it
        if (last_words.includes(',')) {
            last_words = last_words.split(',')[1];
        }

        if (words.length > 8) {
            last_words = last_words.replace(/%./g, '');
            return `${encodeFragment(first_words)},${encodeFragment(last_words)}`;
        } else {
            first_words = first_words.replace(/%./g, '');
            return `${encodeFragment(first_words)}`;
        }
    }

}